import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from 'context/useAuth';
import { useCollection } from 'functions/hooks/useCollection';
import '../Uptime/Uptime.css';

export default function SystemDashboard() {
  const { authorisedUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [error, setError] = useState<string | null>(null);
  
  // User metrics
  const [totalUsers, setTotalUsers] = useState(0);
  const [usersWithVehicles, setUsersWithVehicles] = useState(0);
  const [usersWithLinkedDevices, setUsersWithLinkedDevices] = useState(0);
  const [usersWithoutVehicles, setUsersWithoutVehicles] = useState(0);
  
  // Vehicle metrics
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [vehiclesWithDevices, setVehiclesWithDevices] = useState(0);
  const [vehiclesWithoutDevices, setVehiclesWithoutDevices] = useState(0);
  const [vehiclesNotLinkedToUser, setVehiclesNotLinkedToUser] = useState(0);

  // Firebase collections
  const devicesCollection = useCollection<any>('devices');
  const vehiclesCollection = useCollection<any>('vehicles');
  const usersCollection = useCollection<any>('users');

  // Track latest in-flight request so a refresh click can't be overtaken
  // by a slower earlier response, and so unmount doesn't setState.
  const cancelRef = useRef<{ cancelled: boolean } | null>(null);

  const fetchData = useCallback(async () => {
    // Invalidate any in-flight previous fetch.
    if (cancelRef.current) cancelRef.current.cancelled = true;
    const token = { cancelled: false };
    cancelRef.current = token;

    setLoading(true);
    setError(null);
    setLastRefreshed(new Date());

    try {
      // Parallelise — the 3 collection reads are independent.
      const [vehicles, allDevices, users] = await Promise.all([
        vehiclesCollection.queryMany(),
        devicesCollection.queryMany(),
        usersCollection.queryMany(),
      ]);
      if (token.cancelled) return;

      // O(V+D) device lookup (was O(V*D) via Array.some).
      const deviceIdSet = new Set(allDevices.map(d => d.id));

      let vehiclesWithDevicesCount = 0;
      let vehiclesNotLinkedCount = 0;
      const usersWithVehicleIds = new Set<string>();
      const usersWithLinkedDevicesSet = new Set<string>();

      for (const v of vehicles) {
        const userId: string | undefined = v.data.userId;
        const deviceId: string | undefined = v.data.deviceId;
        const hasUser = !!userId;
        const hasDevice = !!deviceId && deviceIdSet.has(deviceId);

        if (hasUser) usersWithVehicleIds.add(userId!);
        else vehiclesNotLinkedCount++;

        if (hasDevice) {
          vehiclesWithDevicesCount++;
          if (hasUser) usersWithLinkedDevicesSet.add(userId!);
        }
      }

      setTotalUsers(users.length);
      setTotalVehicles(vehicles.length);
      setUsersWithVehicles(usersWithVehicleIds.size);
      setUsersWithoutVehicles(users.length - usersWithVehicleIds.size);
      setVehiclesNotLinkedToUser(vehiclesNotLinkedCount);
      setVehiclesWithDevices(vehiclesWithDevicesCount);
      setVehiclesWithoutDevices(vehicles.length - vehiclesWithDevicesCount);
      setUsersWithLinkedDevices(usersWithLinkedDevicesSet.size);

    } catch (error) {
      if (token.cancelled) return;
      console.error('Error fetching data:', error);
      setError('Failed to load dashboard data. Please try again later.');
    } finally {
      if (!token.cancelled) setLoading(false);
    }
  }, [vehiclesCollection, devicesCollection, usersCollection]);

  useEffect(() => {
    // Most users land on the dashboard and immediately navigate away; the
    // three full-collection reads are heavy, so delay the initial fetch a
    // few seconds and bail entirely if the component unmounts first.
    const initialFetchDelay = setTimeout(() => {
      fetchData();
    }, 3000);
    return () => {
      clearTimeout(initialFetchDelay);
      if (cancelRef.current) cancelRef.current.cancelled = true;
    };
  }, [fetchData]);

  const handleRefresh = () => {
    fetchData();
  };

  const formatLastRefreshed = (date: Date): string => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  if (loading && totalUsers === 0) {
    return <div className="loading">Loading dashboard data...</div>;
  }

  return (
    <div className="uptime-dashboard">
      {error && <div className="error-message">{error}</div>}
      
      <div className="dashboard-header">
        <div className="last-updated">
          Last updated: {formatLastRefreshed(lastRefreshed)}
        </div>
        <div className="header-actions">
          <button 
            className="refresh-button" 
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh Data'}
          </button>
        </div>
      </div>
      
      <h2 className="section-title">Users</h2>
      <div className="uptime-summary">
        <div className="summary-card">
          <h3>Total Users</h3>
          <p className="value">{totalUsers}</p>
          <p className="description">All users in the system</p>
        </div>
        <div className="summary-card">
          <h3>Users with Vehicles</h3>
          <p className="value">{usersWithVehicles}</p>
          <p className="description">Users who own vehicles</p>
        </div>
        <div className="summary-card">
          <h3>Users without Vehicles</h3>
          <p className="value">{usersWithoutVehicles}</p>
          <p className="description">Users with no vehicles</p>
        </div>
        <div className="summary-card">
          <h3>Users with Linked Devices</h3>
          <p className="value">{usersWithLinkedDevices}</p>
          <p className="description">Users with active tracking</p>
        </div>
      </div>
      
      <h2 className="section-title">Vehicles</h2>
      <div className="uptime-summary">
        <div className="summary-card">
          <h3>Total Vehicles</h3>
          <p className="value">{totalVehicles}</p>
          <p className="description">All registered vehicles</p>
        </div>
        <div className="summary-card">
          <h3>Vehicles with Devices</h3>
          <p className="value">{vehiclesWithDevices}</p>
          <p className="description">Vehicles with tracking</p>
        </div>
        <div className="summary-card">
          <h3>Vehicles Without Devices</h3>
          <p className="value">{vehiclesWithoutDevices}</p>
          <p className="description">Not equipped with tracking</p>
        </div>
        <div className="summary-card">
          <h3>Vehicles Not Linked to Users</h3>
          <p className="value">{vehiclesNotLinkedToUser}</p>
          <p className="description">No associated user</p>
        </div>
      </div>
    </div>
  );
} 